import React, { useState, useEffect } from 'react';
import {
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';
import { Box } from '@mui/material';
import { useDriverProfileTabs } from '../../hooks/useDriverProfileTabs';
import { DriverProfileTabsPanel } from '../../components/DriverProfileTabsPanel';
import { type DriverData } from '../../components/driver-profile-tabs';

export const DriverProfileTab = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // Use shared hook for tabs and reference data
  const { tabValue, handleTabChange, licenseTypes, certificationTypes, educationSchools } = useDriverProfileTabs();

  const [driverData, setDriverData] = useState<DriverData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load driver data - server always includes driver in user record
  useEffect(() => {
    if (record?.id) {
      setIsLoading(true);
      
      // Driver data should always be in the user record from the server
      if (record?.driver) {
        setDriverData(record.driver);
      } else {
        // No driver profile exists yet (null is valid)
        setDriverData(null);
      }
      setIsLoading(false);
    }
  }, [record?.id, record?.driver]);

  // Handle saving snapshot data
  const handleSaveSnapshot = async (data: Partial<DriverData>) => {
    try {
      setIsLoading(true);
      await dataProvider.update('drivers', {
        id: driverData?.id,
        data,
        previousData: driverData,
      });
      notify('Profile updated successfully', { type: 'success' });
      refresh();
    } catch (error: any) {
      notify('Error updating profile: ' + (error.message || 'Unknown error'), { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh driver data without clearing the UI
  const handleRefreshDriver = async () => {
    try {
      if (record?.id) {
        // Refresh the user record which includes driver data
        const { data: refreshedUser } = await dataProvider.getOne('users', { id: record.id });
        if (refreshedUser?.driver) {
          setDriverData(refreshedUser.driver);
        } else {
          setDriverData(null);
        }
      }
    } catch (error: any) {
      console.error('Error refreshing driver:', error);
      notify('Error refreshing profile', { type: 'warning' });
    }
  };

  return (
    <Box>
      <DriverProfileTabsPanel
        tabValue={tabValue}
        onTabChange={handleTabChange}
        driverData={driverData}
        isLoading={isLoading}
        licenseTypes={licenseTypes}
        certificationTypes={certificationTypes}
        educationSchools={educationSchools}
        onSaveSnapshot={handleSaveSnapshot}
        onRefreshDriver={handleRefreshDriver}
      />
    </Box>
  );
};
